import React, { useState } from 'react';
import { Button } from '../Button';
import { Box } from '../Layout';

export interface ReactionButtonsProps {
  /** Initial like count */
  likeCount?: number;
  /** Initial reply count */
  replyCount?: number;
  /** Initial share count */
  shareCount?: number;
  /** Whether the item is initially liked */
  isLiked?: boolean;
  /** Whether to show counts */
  showCounts?: boolean;
  /** Size of the buttons */
  size?: 'xs' | 'sm' | 'md';
  /** Alignment of buttons */
  align?: 'left' | 'center' | 'right';
  /** Callbacks */
  onLike?: (isLiked: boolean) => void;
  onReply?: () => void;
  onShare?: () => void;
  /** Custom styling */
  className?: string;
  /** Show on hover only */
  showOnHover?: boolean;
}

export const ReactionButtons: React.FC<ReactionButtonsProps> = ({
  likeCount = 0,
  replyCount = 0,
  shareCount = 0,
  isLiked: initialIsLiked = false,
  showCounts = true,
  size = 'xs',
  align = 'left',
  onLike,
  onReply,
  onShare,
  className = '',
  showOnHover = false
}) => {
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const [currentLikeCount, setCurrentLikeCount] = useState(likeCount);

  const handleLike = () => {
    const newIsLiked = !isLiked;
    setIsLiked(newIsLiked);
    setCurrentLikeCount(prev => newIsLiked ? prev + 1 : prev - 1);
    onLike?.(newIsLiked);
  };

  const alignmentClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end'
  };

  const visibilityClass = showOnHover 
    ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-200' 
    : '';

  return (
    <Box className={`flex items-center space-x-1 ${alignmentClasses[align]} ${visibilityClass} ${className}`}>
      {/* Like Button */}
      <Button
        size={size}
        variant={isLiked ? "primary" : "ghost"}
        onClick={handleLike}
        className="px-2 py-1 hover:bg-red-50 hover:text-red-500 transition-colors"
        aria-label={`${isLiked ? 'Unlike' : 'Like'} this post`}
      >
        <span className="mr-1">
          {isLiked ? '❤️' : '🤍'}
        </span>
        {showCounts && <span>{currentLikeCount}</span>}
      </Button>

      {/* Reply Button */}
      <Button
        size={size}
        variant="ghost"
        onClick={onReply}
        className="px-2 py-1 hover:bg-blue-50 hover:text-blue-500 transition-colors"
        aria-label="Reply to this post"
      >
        <span className="mr-1">💬</span>
        {showCounts && <span>{replyCount}</span>}
      </Button>

      {/* Share Button */}
      <Button
        size={size}
        variant="ghost"
        onClick={onShare}
        className="px-2 py-1 hover:bg-green-50 hover:text-green-500 transition-colors"
        aria-label="Share this post"
      >
        <span className="mr-1">🔗</span>
        {showCounts && <span>{shareCount}</span>}
      </Button>
    </Box>
  );
};

export default ReactionButtons;